/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useEffect } from 'react';
// redux
import { useDispatch, useSelector } from 'react-redux';
// thunks
import { PRODUCT_COMMENTING, PRODUCT_COMMENTING_V2 } from '../../../../../state/thunks/account';
// utils
import { url } from '../../../../../utils/urls';
// material
import { Page, SingleSelectDropDown, TextField, Title, Button, Group, EmptyState, Tip, history, log } from '@insticator/insticator-ui'; // iui-material
// styles
import injectSheet from 'react-jss';
import styles from './importpreview_styles';
import { getTopThreadsExamples, getTopArticlesExamples } from '../../../../../state/thunks/commenting';

/* ========== ~~~~~~~~~~ IMPORT PREVIEW ~~~~~~~~~~ ========== */
const ImportPreview = props => {
  const { integrationId } = props.match.params

  const { accountProducts, accountProductRequestStatus } = useSelector(state => state.account) 
  const { accountUUID, siteUUID } = useSelector((state) => state.session)
  const hasCommentingV2Access = accountProducts.includes(PRODUCT_COMMENTING_V2);
  const hasCommentingAccess = accountProducts?.includes(PRODUCT_COMMENTING);
  const dispatch = useDispatch();

  const [articleURLs, setArticleURLs] = useState([])
  const [selectedArticle, selectArticle] = useState(null)
  const [limit, setLimit] = useState(5)
  const [isLivePageCopied, copyLivePage] = useState(false)
  const [isDemoPageCopied, copyDemoPage] = useState(false)

  const handleSelectArticle = opt => {
    const currentArticle = articleURLs.filter(article => article.livePageUrl === opt.value).pop()
    selectArticle(currentArticle)
  } 

  const fetchTopThreadExamples = useCallback(async() =>{
    let response = {}

    if(hasCommentingV2Access) response = await dispatch(getTopArticlesExamples({commentingUnitId: integrationId, limit}))
    else response = await dispatch(getTopThreadsExamples({integrationId, limit}))

    let data = response.payload || []
    if(data.length > 0) {
      if(hasCommentingV2Access) {
        data = data.map(item => {
          const pageId = item.extPageId ? `pageId=${encodeURI(item.extPageId)}` : 'skipPageIdCheck=true' // to allow previewing articles without page id
          const demoURL = url.commentingV2.preview + `?${pageId}&pageUrl=${encodeURIComponent(item.pageUrl)}&pageTitle=${encodeURI(item.pageTitle.trim())}&siteUuid=${encodeURI(siteUUID)}&cuid=${encodeURI(integrationId)}&accountUuid=${encodeURI(accountUUID)}`
          log('demo URL', demoURL)
          return {
            ...item,
            livePageUrl: item.pageUrl,
            demoPageUrl: demoURL
          }
      })
      }
      selectArticle(data[0])
      setArticleURLs(data)
    }
}, [accountUUID, dispatch, hasCommentingV2Access, integrationId, limit, siteUUID]);


  useEffect(() => {
    fetchTopThreadExamples();
  },[fetchTopThreadExamples, integrationId])

 
  const loadThreads = () => fetchTopThreadExamples()

  const renderIframe = url => `<iframe src="${url}" height="100%" width="100%"></iframe>`
  
  const openNewTab = url => window.open(url)

  const copyURL = (url, pageType) => {
    var input = document.createElement('input');
    input.setAttribute('value', url);
    document.body.appendChild(input);
    input.select();
    var result = document.execCommand('copy');
    document.body.removeChild(input);

    if(pageType === 'live') copyLivePage(true)
    else copyDemoPage(true)

    setTimeout(() => {
      if(pageType === 'live') copyLivePage(false)
      else copyDemoPage(false)
    }, 1200)
    return;
  }

  const handleThreadsCountChange = e => {
    const text = e.target.value;
    const NUMBER_REGEXP = /[0-9\/]+/;
    if(parseInt(text) > 10) return;
    if(text.length > 2) return; 
    if(!NUMBER_REGEXP.test(text) && text) e.preventDefault()
    else setLimit(e.target.value)
  }

  if(['idle', 'pending'].includes(accountProductRequestStatus)) return null;
  
  return hasCommentingAccess || hasCommentingV2Access ? (
      <Page name={`importpreview`} klass={props.classes.importpreview}>
        <Title><h5>Imported Comment Preview</h5></Title>
        <div className={'importpreviewheader'}>
          { articleURLs &&
            <div className={'articleurlsdropdown'}>
              <label>Top Thread</label>
              <SingleSelectDropDown 
                name={selectedArticle ? `${selectedArticle.livePageUrl}` : ''} 
                searchable={false} 
                onSelect={handleSelectArticle} 
                items={articleURLs.map(article => ({ name: `${article.livePageUrl}`, value: article.livePageUrl }))}
              />
            </div>
          }
          <div className={'threadscountwrapper'}>
            <TextField
              type={`number`} 
              name={`threadscount`} 
              value={limit} 
              handleChange={handleThreadsCountChange} 
              label={
                <span>
                  Top Threads
                  <Tip 
                    space={'left'} 
                    icon={'question-circle'} 
                    content={(<span>Top threads count (1-10)</span>)}
                  />
                </span>
              }
              maxLength={2}
              min={1}
              max={10}
            />
            <Button name={'loadbtn'} variant={'primary'} text={'Go'} handleClick={loadThreads} disabled={!limit || parseInt(limit) === 0} />
          </div>
        </div>

        { selectedArticle &&
        <div className={'previewcontainer'}>
          <div className={'pagepreview'}>
            <div className={'pagepreviewheader'}>
              <span classNam>Original URL
              <Tip 
                space={'left'} 
                icon={'question-circle'} 
                content={(
                <span>
                  Certain pages might have some problem loading in the preview iframe due to <br />
                  <a href={'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Security-Policy'}>Content Security Policy</a> restricting 
                  them to only frame the page within the same host/domain. 
                </span>)}
                label={'Trouble loading the page?'} 
              />
              </span>
             
              <Group>
                <Button variant={'primary'} text={isLivePageCopied ? 'copied!' : 'copy'} disabled={isLivePageCopied} handleClick={() => copyURL(selectedArticle.livePageUrl, 'live')} />
                <Button variant={'primary'} text={'open'} handleClick={() => openNewTab(selectedArticle.livePageUrl)} />
              </Group>
            </div>
            <div className="livepage iframe" dangerouslySetInnerHTML={{__html: renderIframe(selectedArticle.livePageUrl)}}></div>
          </div>
          <div className='pagepreview'>
            <div className={'pagepreviewheader'}>
              <span>Demo URL</span>
              <Group>
                <Button variant={'primary'} text={isDemoPageCopied ? 'copied!' : 'copy'} disabled={isDemoPageCopied}  handleClick={() => copyURL(selectedArticle.demoPageUrl, 'demo')} />
                <Button variant={'primary'} text={'open'} handleClick={() => openNewTab(selectedArticle.demoPageUrl)} />
              </Group>
            </div>
            <div className="demopage iframe" dangerouslySetInnerHTML={{__html: renderIframe(selectedArticle.demoPageUrl)}}></div>
          </div>
        </div>
        }
      </Page>
  ) 
  :
  (
    <Page name={`importpreview`} klass={props.classes.importpreview}>
      <Title klass={'previewtitle'}><h5>Imported Comment Previews</h5></Title>
      <EmptyState 
        graphicName={'error-404'} 
        buttonText={'Go to Request Commenting Unit Page'} 
        buttonClick={() => history.push('/dashboard/commenting/requestaccess')} 
        bodycopy={<p>Commenting is not yet enabled. Request access to enable import preview.</p>} 
      />
   </Page>
  )
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(ImportPreview);
